import { History, RefreshCw, CheckCircle2, AlertTriangle, Clock, Database, Activity } from 'lucide-react';
import { useTenant } from '@/context/TenantContext';
import { useGet } from '@/lib/useData';
import { PageHeader } from '@/components/common/PageHeader';
import { SectionCard } from '@/components/common/SectionCard';
import { CardSkeleton } from '@/components/common/Loading';
import { EmptyState } from '@/components/common/EmptyState';
import { Badge } from '@/components/ui/badge';
import { fmtInt, fmtDateTime } from '@/lib/format';

const STATUS = {
  success: { label: 'Succès', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200', Icon: CheckCircle2 },
  running: { label: 'En cours', cls: 'bg-sky-50 text-sky-700 ring-sky-200', Icon: RefreshCw },
  awaiting_ids: { label: 'En attente d\'IDs', cls: 'bg-amber-50 text-amber-800 ring-amber-200', Icon: Clock },
  error: { label: 'Erreur', cls: 'bg-rose-50 text-rose-700 ring-rose-200', Icon: AlertTriangle },
};

export default function Historique() {
  const { current } = useTenant();
  const health = useGet('/sync/health', undefined, true);
  const hist = useGet('/sync/history', { association: current }, !!current);
  const jobs = hist.data?.jobs || [];
  const h = health.data;

  return (
    <div className="space-y-6">
      <PageHeader title="Historique" icon={History} subtitle="Synchronisations, journal et état de l'API Evergiving" />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <SectionCard title="État de l'API Evergiving">
          {health.isLoading ? <CardSkeleton height="h-16" /> : (
            <div className="flex items-center gap-3">
              <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${h?.ok ? 'bg-emerald-50' : 'bg-rose-50'}`}>
                <Activity className={`h-5 w-5 ${h?.ok ? 'text-emerald-600' : 'text-rose-600'}`} />
              </div>
              <div>
                <p className="font-display text-sm font-semibold">{h?.ok ? 'Opérationnelle' : 'Indisponible'}</p>
                <p className="text-xs text-muted-foreground">{h?.note}</p>
              </div>
            </div>
          )}
        </SectionCard>
        <SectionCard title="Enregistrements importés">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl accent-bg-100"><Database className="h-5 w-5 accent-text" /></div>
            <p className="font-display text-2xl font-semibold tabular">{fmtInt(jobs.reduce((s, j) => s + (j.records_imported || 0), 0))}</p>
          </div>
        </SectionCard>
        <SectionCard title="Dernière synchronisation">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl accent-bg-100"><RefreshCw className="h-5 w-5 accent-text" /></div>
            <p className="text-sm font-medium">{jobs[0] ? fmtDateTime(jobs[0].finished_at || jobs[0].started_at) : '—'}</p>
          </div>
        </SectionCard>
      </div>

      <SectionCard title="Journal des synchronisations" subtitle="Historique complet des imports et synchronisations">
        {hist.isLoading ? <CardSkeleton /> : jobs.length === 0 ? <EmptyState body="Aucune synchronisation enregistrée." /> : (
          <div className="space-y-3">
            {jobs.map((j) => {
              const st = STATUS[j.status] || STATUS.success;
              return (
                <div key={j.id} className="flex items-start gap-3 rounded-xl border bg-card p-3" data-testid="sync-job-row">
                  <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg accent-bg-100">
                    <st.Icon className="h-4 w-4 accent-text" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium">{j.type === 'excel_import' ? 'Import Excel historique' : 'Synchronisation Evergiving'}</span>
                      <Badge className={`rounded-full ring-1 ${st.cls}`} variant="outline">{st.label}</Badge>
                      {j.source && <span className="font-mono text-[10px] text-muted-foreground">{j.source}</span>}
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {fmtDateTime(j.started_at)} · {fmtInt(j.records_imported || 0)} nouveaux
                      {j.type === 'evergiving' && j.records_updated != null ? ` · ${fmtInt(j.records_updated)} mis à jour` : ' enregistrements'}
                      {j.triggered_by === 'auto' && <span className="ml-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium">auto</span>}
                    </p>
                    {j.errors?.length > 0 && <p className="mt-1 text-xs text-rose-600">{j.errors.join(', ')}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
